export interface ApiError {
  code: string;
  message: string;
  metadata?: Record<string, unknown>;
}

export interface ApiErrorResponse {
  status: number;
  error: ApiError;
}

/**
 * Backend business error codes the UI reacts to.
 *
 * Adding a member here is only half the job: every code MUST also have French
 * copy in `BUSINESS_ERROR_MESSAGES` (`@core/errors/business-error-messages`).
 * That map is typed `Record<ErrorCode, string>`, so a member without copy is a
 * compile error rather than a generic message in production.
 *
 * Codes deliberately absent: the PIN codes "1052" (wrong PIN) and "1061"
 * (locked). Those carry a `remaining_attempts` count in their server message
 * that no static copy can reproduce, so they are surfaced verbatim.
 */
export enum ErrorCode {
  MERCHANT_NOT_FOUND = '321',
  INVALID_PASSWORD = '447',
  /** OTP entered during PIN recovery is invalid. */
  INVALID_OTP = '047',
  /** Merchant account is suspended — transactional operations are rejected. */
  ACCOUNT_SUSPENDED = '045',
  /** Session is no longer valid for a money operation (wallet flows). */
  SESSION_EXPIRED = '451',
  /**
   * No Gozem Money account matches the identifier sent. Seen on `auth_txn` /
   * `init_psp_transfer` when the beneficiary phone resolves to no wallet.
   */
  GOZEM_MONEY_USER_NOT_FOUND = '1032',
}

/** User-facing message shown when a merchant account is suspended. */
export const ACCOUNT_SUSPENDED_MESSAGE =
  'Votre compte a été suspendu. Veuillez contacter le support.';

/**
 * Width every backend business code is written to.
 *
 * Three, because the shortest codes the app compares against are the
 * leading-zero pair "047" / "045". Nothing in the codebase — neither
 * {@link ErrorCode} nor the lead-form codes ("358", "456", "401") — is shorter
 * than this, which is what makes the padding in {@link normalizeErrorCode} safe.
 */
const ERROR_CODE_WIDTH = 3;

/** Plain digit strings only — see the padding rule in {@link normalizeErrorCode}. */
const DIGITS_ONLY = /^\d+$/;

/**
 * Normalize a backend `error_code` to a string for comparison against {@link ErrorCode}.
 *
 * The payment/auth APIs are inconsistent: some endpoints return the code as a JSON
 * number (e.g. `447` for an invalid password), others as a string (e.g. `"047"`).
 * Strict comparisons against the string-valued {@link ErrorCode} enum silently fail
 * on the numeric form, so callers must normalize before comparing. Coercing to a
 * string here gives one reliable comparison point across the app.
 *
 * Coercing alone is not enough for the leading-zero codes. `ErrorCode.INVALID_OTP`
 * is "047", but a JSON `47` coerces to "47" and matches nothing: the recover-PIN
 * flow then loses its only invalid-code branch and strands the user on the final
 * step behind a generic "Une erreur est survenue" with no way forward (GPGT-40873).
 * So digit-only codes are left-padded to {@link ERROR_CODE_WIDTH}.
 *
 * Padding cannot change any existing outcome: every code the app compares against
 * is at least three digits, so a shorter one matches nothing today. Longer codes
 * ("1032") are already wider than the pad width and pass through untouched.
 *
 * Returns `null` for absent, empty, or non-finite codes so a missing code never
 * accidentally matches a known {@link ErrorCode}.
 */
export function normalizeErrorCode(code: unknown): string | null {
  if (code === null || code === undefined || code === '') {
    return null;
  }

  let raw: string;
  if (typeof code === 'number') {
    if (!Number.isFinite(code)) {
      return null;
    }
    raw = String(code);
  } else if (typeof code === 'string') {
    raw = code;
  } else {
    return null;
  }

  // Pad plain digit strings only. A negative, fractional or non-numeric code
  // ("-5", "47.5", "ERR_FOO") is passed through rather than mangled into
  // something that could collide with a real code.
  return DIGITS_ONLY.test(raw) ? raw.padStart(ERROR_CODE_WIDTH, '0') : raw;
}
